import { useCallback, useMemo } from 'react'

import { Dropdown } from 'core-components/library/dropdown'
import { IRenderCustomSettingsParams } from 'core-components/sitebuilder/layout'
import { IFromLibrarySettings } from 'core-components/sitebuilder/models/IWidget'
import { IProperties, PropertyGenerator } from 'core-components/sitebuilder/propertygenerator'

import { libraryTypeSource } from './constants/libraryTypeSource'

interface IBasicSettingsLibrary extends IRenderCustomSettingsParams {
  getPropertiesByType: (type: IFromLibrarySettings['type']) => IProperties[]
}

export const BasicSettingsLibrary = ({
  widget,
  onChangeField,
  getPropertiesByType,
}: IBasicSettingsLibrary) => {
  const fromLibrarySettings = (widget.settings || {}) as IFromLibrarySettings

  const properties = useMemo<IProperties[]>(() => {
    const baseProperties = getPropertiesByType(fromLibrarySettings.type)

    if (fromLibrarySettings.properties) {
      const userProperties = JSON.parse(
        JSON.stringify(fromLibrarySettings.properties)
      ) as IProperties[]
      return baseProperties.map((base) => {
        const element = userProperties.find((el) => el.name === base.name)
        if (element) {
          const keys = Object.keys(base)
          for (const key of keys) {
            const ignoredKeys = ['customEditor', 'enumValues'] // todo: remove after change all paths
            const arrayKeys = ['objectDescription']

            // for array property if we add new elements we need to update just exist items
            if (element[key] !== undefined && !ignoredKeys.includes(key)) {
              const baseObject = base[key] as IProperties['objectDescription']
              const elementObject = element[key] as IProperties['objectDescription']
              if (arrayKeys.includes(key)) {
                for (const elItem of elementObject!) {
                  const index = baseObject!.findIndex((el) => el.name === elItem.name)
                  if (index > -1) {
                    baseObject![index] = elItem
                  }
                }
              } else {
                base[key] = element[key]
              }
            }
          }
        }
        return base
      })
    }
    return baseProperties
  }, [fromLibrarySettings.properties, fromLibrarySettings.type, getPropertiesByType])

  const saveHandler = useCallback(
    async (properties: IProperties[]) => {
      function replacer(key: string, value: unknown) {
        // didn't check crud instance
        if (key == 'enumSource') return undefined
        return value
      }
      if (
        JSON.stringify(properties, replacer) !==
        JSON.stringify(widget.settings ? widget.settings['properties'] : [], replacer)
      ) {
        const newSettings = { ...widget.settings, properties }
        onChangeField('settings', newSettings)
      }
    },
    [onChangeField, widget.settings]
  )

  return (
    <div className={'margin-top-md'}>
      <Dropdown
        caption={'label'}
        source={libraryTypeSource}
        label="Select widget"
        value={fromLibrarySettings.type as string}
        onChange={(value) => {
          if (value) {
            onChangeField('settings', { type: value, properties: [] })
          }
        }}
      />
      <PropertyGenerator
        key={fromLibrarySettings.type}
        properties={properties}
        autoSave={true}
        onSave={saveHandler}
      />
    </div>
  )
}
